import { logger } from '@adornis/base/logging.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { randomUUID } from 'node:crypto';
import { LLM_PAYLOAD_SAFE_LIMIT_BYTES } from '../../../shared/constants.js';
import { GRAMMAR_TASK_TYPES } from '../../../shared/grammar-grading.js';
import {
  gsGenerationAgentModel,
  gsGenerationAgentReasoningEffort,
  gsGenerationAgentTemperature,
} from '../../../shared/models.js';
import { Exam, GradingScheme, LanguageUseScheme } from '../../exam.js';
import {
  addSolutionChangedTasks,
  dropSolutionTasks,
  markSolutionTasksDone,
  markSolutionTaskStarted,
  setSolutionMessage,
  setSolutionStatus,
  setSolutionTotal,
  startSolutionProgress,
  StepState,
} from '../../extraction-progress.js';
import { buildGradingSchemePrompts, buildLanguageUseCriteriaRefinementPrompts } from '../../prompts.js';
import {
  computeAggregate,
  dropDistributedParentTotals,
  effectivePoints,
  isAggregator,
  statedTotal,
} from '../../utils/points-model.js';
import { refreshSolutionSourceState } from '../../utils/solution-source.js';
import {
  appendTaskRefSegment,
  normalizeTaskRef,
  normalizeTaskRefs,
  normalizeTaskRefSegment,
} from '../../utils/task-ref.js';
import { addCostToExam } from '../add-cost.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { updateSetupStep } from '../pending-tasks/extraction-or-generation-finished.js';
import { TaskType, taskTypeBehaviours } from '../task-types/task-main.js';
import { genericLanguageUseGroupTemplate, languageUseTemplateFor } from '../utils/language-use-template-resolution.js';
import { resolveLngCode } from '../utils/system-info.js';
import { persistRootSubtree } from './persist-task-writes.js';
// Imported directly from the tool module (not the tools barrel) to avoid the barrel's
// grading-scheme.tool → gs-generation-agent import cycle. memory.tool has no back-import here.
import { Conversation } from '../../conversation.js';
import { getTeacherMemoryInstructionBlock } from '../tools/memory.tool.js';
import {
  collectAllTaskAdditionalInfoIds,
  normalizeAdditionalInfoForPrompt,
  resolveAdditionalInfoForTask,
} from './additional-info-context.js';
import {
  type AgentPayloadPart,
  assembleHierarchyPayload,
  clampPayloadToByteLimit,
  type HierarchyTaskInput,
  imageIdsAsBoxes,
  type PayloadBox,
} from './agent-payload.js';
import {
  buildPathMap,
  collectSubtree,
  collectSubtreeTaskTypes,
  getTaskKey,
  gradableSubtreeNodes,
  gsTargets,
  hasGradingSchemeContent,
  isGradable,
  type NestedExamTask,
  solutionUnitNodeCount,
  solutionUnitRoots,
  subtreeMasterSolutionReady,
  type SubtreeNode,
} from './tree-generation-utils.js';

/** One grading scheme per gradable task in the subtree, keyed by the task header id (path). */
export interface GsResultEntry {
  id: string;
  criteria: string;
  maxPoints: number;
}

async function generateAiGradingScheme(
  systemPrompt: string,
  userPrompt: string,
  materialParts: AgentPayloadPart[] = [],
): Promise<{ results: GsResultEntry[]; cost?: number }> {
  const router = getLLMRouter();
  try {
    const TIMEOUT_MS = 300000; // 5 minutes
    const timeoutPromise = new Promise<{ results: GsResultEntry[] }>((_, reject) => {
      setTimeout(() => {
        reject(new Error('TIMEOUT'));
      }, TIMEOUT_MS);
    });

    // The context is supplied as labeled, interleaved parts in the user message (task description,
    // additional information, master solution, and — when refining — the current grading scheme).
    const parts = await clampPayloadToByteLimit(materialParts, LLM_PAYLOAD_SAFE_LIMIT_BYTES);
    const userContent: any = parts.length > 0 ? [{ type: 'text', text: userPrompt }, ...parts] : userPrompt;

    const responsePromise = router.chat.completions.create({
      stream: false,
      model: gsGenerationAgentModel,
      provider: 'openrouter',
      temperature: gsGenerationAgentTemperature,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userContent },
      ],
      response_format: { type: 'json_object' } as any,
      // OpenRouter reasoning control — forwarded verbatim by the router (token.js does not whitelist params).
      ...({ reasoning: { effort: gsGenerationAgentReasoningEffort } } as any),
    });

    responsePromise.catch(err => {
      logger.warn({ err }, 'Background responsePromise rejected after timeout or cancellation.');
    });

    let response;
    try {
      response = await Promise.race([responsePromise, timeoutPromise]);
    } catch (error: any) {
      if (error?.message === 'TIMEOUT') {
        logger.warn('Timeout bei der Generierung des Bewertungsschlüssels.');
        return { results: [] };
      }
      throw error;
    }

    const rawContent = response.choices[0]?.message?.content || '{}';
    const parsedData = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;

    const gsCost = Number(response?.usage?.cost ?? 0);

    const results: GsResultEntry[] = Array.isArray(parsedData?.results)
      ? parsedData.results
          .filter((r: any) => r && r.id != null)
          .map((r: any) => ({
            id: String(r.id),
            criteria: String(r.criteria ?? ''),
            maxPoints: Number(r.maxPoints) || 0,
          }))
      : [];

    return { results, cost: gsCost };
  } catch (error) {
    logger.error({ err: error }, 'LLM Aufruf fehlgeschlagen');
    throw error;
  }
}

interface GsHierarchyBuild {
  subtree: SubtreeNode[];
  targets: SubtreeNode[];
  targetIds: Set<string>;
  targetTaskIds: string[];
  hierarchyInputs: HierarchyTaskInput[];
}

/**
 * Builds the per-node hierarchy inputs (task description, additional info, master solution, and current
 * scheme-when-refining) for one root subtree — the exact material a generation call for this root sends.
 * Extracted out of `generateGradingSchemeForTree` so run + refine share one implementation. Deliberately
 * does NOT replicate the language-use backfill gating in `generateGradingSchemeForTree` — that decides
 * whether there's additional non-payload work to do, not what the payload itself contains.
 */
function buildGsHierarchyInputs(
  ctx: GsTreeContext,
  root: NestedExamTask,
  opts: { feedback?: string; targetKeys?: Set<string> } = {},
): GsHierarchyBuild {
  const { allAdditionalInfo, allTaskAdditionalInfoIds } = ctx;
  const isRefine = !!opts.feedback;
  const subtree = collectSubtree(root);
  const targets = subtree.filter(s => {
    if (!isGradable(s.node)) return false;
    if (opts.targetKeys && !opts.targetKeys.has(getTaskKey(s.node))) return false;
    return isRefine || !hasGradingSchemeContent(s.node);
  });
  const targetIds = new Set(targets.map(s => s.id));
  const targetTaskIds = targets.map(s => getTaskKey(s.node));

  const hierarchyInputs: HierarchyTaskInput[] = subtree.map(s => {
    const node = s.node;
    const info = resolveAdditionalInfoForTask(node, allAdditionalInfo, allTaskAdditionalInfoIds);
    const msNode = ((node as any).masterSolution?.doc ?? (node as any).masterSolution) || {};
    const gsNode = ((node as any).gradingScheme?.doc ?? (node as any).gradingScheme) || {};
    const isTarget = targetIds.has(s.id);
    const taskType = typeof (node as any).taskType === 'string' ? (node as any).taskType : undefined;
    // This node's OWN target: an answerable node's own points (its scheme value, or declared for a leaf);
    // a task frame's declared total. This is the criteria the model should write for THIS node.
    const approvedTotal = statedTotal(node);
    // What this node's subtasks must sum to = its subtree total minus its OWN points. For a task frame own
    // is 0, so children cover the whole total; for an answerable PARENT the children cover total − own, so
    // own + children = total. Its own points come from its grading scheme (0 if none / top-down).
    const hasKids = Array.isArray((node as any).subTasks) && (node as any).subTasks.length > 0;
    const nodeTotal = computeAggregate(node);
    const ownPortion =
      node.needsAnswer !== false && hasKids && typeof gsNode.maxPoints === 'number' ? Number(gsNode.maxPoints) : 0;
    const childrenTarget = nodeTotal - ownPortion;
    // On generation, remind the model how the points split. For an answerable PARENT that is ALSO a target
    // (it has its own answer AND subtasks), spell out BOTH numbers so it doesn't confuse own with total:
    // its OWN answer is worth `ownPortion` (write criteria for exactly that), and the subtasks sum to
    // `childrenTarget`, so own + subtasks = the task total. A task frame (own 0) just distributes the total.
    const note =
      !isRefine && childrenTarget > 0.001 && gradableSubtreeNodes(node).length > 0
        ? ownPortion > 0.001
          ? `>> REQUIRED: this task is worth ${nodeTotal} points TOTAL, split as its OWN answer (${ownPortion} points — write this task's criteria for EXACTLY ${ownPortion}, not ${nodeTotal}) PLUS its TARGET subtasks, which MUST sum to EXACTLY ${childrenTarget} (half-points allowed, weighted by complexity). <<`
          : `>> REQUIRED: the "maxPoints" of the TARGET subtasks under this task MUST sum to EXACTLY ${childrenTarget}. Distribute the points across them (half-points allowed, weighted by complexity) so their total matches exactly. <<`
        : undefined;
    const entry: HierarchyTaskInput = {
      id: s.id,
      hierarchy: isTarget ? 'TARGET' : 'CONTEXT',
      taskType,
      maxPoints: approvedTotal,
      note,
      taskDescription: { boxes: (node as any).boundingBoxes as PayloadBox[] | undefined, text: node.description },
      additionalInformation:
        info.length > 0
          ? {
              boxes: info.flatMap(i => imageIdsAsBoxes(i.imageIds)),
              text: info
                .map(i => i.content)
                .filter(Boolean)
                .join('\n\n'),
            }
          : undefined,
      masterSolution: {
        boxes: msNode.boundingBoxes as PayloadBox[] | undefined,
        text: msNode.content || 'Keine Musterlösung vorhanden.',
      },
    };
    if (isRefine && gsNode.criteria) {
      entry.gradingScheme = {
        boxes: gsNode.boundingBoxes as PayloadBox[] | undefined,
        text: gsNode.criteria,
        maxPoints: gsNode.maxPoints,
      };
      entry.current = true;
    }
    return entry;
  });

  return { subtree, targets, targetIds, targetTaskIds, hierarchyInputs };
}

interface GsTreeContext {
  examDocId: string;
  examContext: { subject?: string; topic?: string; classLevel?: string };
  teacherGsRules: string;
  userLanguage: string;
  allAdditionalInfo: ReturnType<typeof normalizeAdditionalInfoForPrompt>;
  allTaskAdditionalInfoIds: ReturnType<typeof collectAllTaskAdditionalInfoIds>;
  inFlight: Set<string>;
  // Task paths (e.g. "2", "3.a") where the agent's subtask distribution ended up differing from the
  // task frame's stated sheet total. Collected across roots and announced once at the end as a deliberate
  // "adjusted to reflect relative complexity" note — never surfaced as an arithmetic error.
  pointAdjustments: Set<string>;
}

// --- Language-use scheme generation (parity with the language-use rules an uploaded GS extraction produces) ---

/** Rounds to the nearest half-point, matching the "half-points allowed" convention used elsewhere here. */
function roundToHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

/** Fraction of a language-use-eligible node's own points allocated to language-use, or undefined if the node's
 * task type isn't language-use-eligible or defines no ratio. */
function languageUseRatioFor(taskType: string | undefined): number | undefined {
  if (!taskType || !GRAMMAR_TASK_TYPES.has(taskType)) return undefined;
  const ratio = (taskTypeBehaviours as any)[taskType]?.grammarPointsRatio;
  return typeof ratio === 'number' && ratio > 0 && ratio < 1 ? ratio : undefined;
}

/** Whether this task already has a language-use rule — membership in an existing Exam.languageUseSchemeRules
 * entry (solo or multi-member) — so this run must leave it alone (upload / a prior run owns it). */
function hasExistingLanguageUseContent(qualifiedPath: string, existingLanguageUseSchemeRules: any[]): boolean {
  return existingLanguageUseSchemeRules.some(
    scheme => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.includes(qualifiedPath),
  );
}

/** Whether `node` itself is TASK_FRAME (transparent to language-use homogeneity) or language-use-eligible, AND every
 * descendant recursively satisfies the same — the "all-or-nothing" condition for grouping a subtree into
 * one LanguageUseScheme. TASK_FRAME nodes are structural pass-through: they never block homogeneity and are
 * never cluster members themselves. */
function allNonTaskFrameEligible(node: NestedExamTask): boolean {
  const taskType = typeof (node as any).taskType === 'string' ? (node as any).taskType : undefined;
  const okSelf = taskType === TaskType.TASK_FRAME || (!!taskType && GRAMMAR_TASK_TYPES.has(taskType));
  if (!okSelf) return false;
  return (Array.isArray(node.subTasks) ? node.subTasks : []).every(allNonTaskFrameEligible);
}

/** Whether `node`'s subtree contains at least one node whose OWN task type is language-use-eligible — guards
 * against treating an all-TASK_FRAME subtree (no real answerable content) as a homogeneous cluster. */
function hasEligibleDescendant(node: NestedExamTask): boolean {
  const taskType = typeof (node as any).taskType === 'string' ? (node as any).taskType : undefined;
  if (taskType && GRAMMAR_TASK_TYPES.has(taskType)) return true;
  return (Array.isArray(node.subTasks) ? node.subTasks : []).some(hasEligibleDescendant);
}

interface LanguageUseClusterMember {
  node: NestedExamTask;
  qualifiedPath: string;
  taskType: string;
  languageUseShare: number;
}

/** The language-use criteria text for a cluster: when every member shares the same task type, that type's own
 * (genre-specific) template — otherwise the generic type-agnostic group template, since group grading is
 * holistic and a per-type template couldn't be attributed to one member's portion of the combined text. */
function languageUseTemplateForCluster(members: LanguageUseClusterMember[], lng: string): string {
  const firstType = members[0]?.taskType;
  const allSameType = !!firstType && members.every(m => m.taskType === firstType);
  return allSameType ? languageUseTemplateFor(firstType, lng) : genericLanguageUseGroupTemplate(lng);
}

/**
 * Walks `node`'s subtree top-down, cutting at the first node whose whole subtree is homogeneously
 * language-use-eligible (per the all-or-nothing rule above) and collecting every member with a pending language-use
 * share (`languageUseShareByPath`, populated while building the hierarchy inputs — this is what scopes
 * clusters to nodes actually being generated this run and excludes anything upload / a prior run already
 * covers). A cut subtree is never recursed into further. Outside any cut subtree, an individually
 * eligible node with a pending share is pushed to `standalone` regardless of its neighbors.
 */
function collectLanguageUseClusters(
  node: NestedExamTask,
  parentPath: string[],
  languageUseShareByPath: Map<string, number>,
  clusters: LanguageUseClusterMember[][],
  standalone: LanguageUseClusterMember[],
): void {
  const path = [...parentPath, String((node as any).taskNumber ?? '')];
  const qualifiedPath = path.join('|');

  if (allNonTaskFrameEligible(node) && hasEligibleDescendant(node)) {
    const members: LanguageUseClusterMember[] = [];
    const collectMembers = (n: NestedExamTask, p: string[]) => {
      const np = [...p, String((n as any).taskNumber ?? '')];
      const nqp = np.join('|');
      const taskType = typeof (n as any).taskType === 'string' ? (n as any).taskType : undefined;
      if (taskType && taskType !== TaskType.TASK_FRAME && languageUseShareByPath.has(nqp)) {
        members.push({ node: n, qualifiedPath: nqp, taskType, languageUseShare: languageUseShareByPath.get(nqp)! });
      }
      (Array.isArray(n.subTasks) ? n.subTasks : []).forEach(c => collectMembers(c, np));
    };
    collectMembers(node, parentPath);
    if (members.length > 1) clusters.push(members);
    else if (members.length === 1) standalone.push(...members);
    return;
  }

  (Array.isArray(node.subTasks) ? node.subTasks : []).forEach(child =>
    collectLanguageUseClusters(child, path, languageUseShareByPath, clusters, standalone),
  );

  const taskType = typeof (node as any).taskType === 'string' ? (node as any).taskType : undefined;
  if (taskType && taskType !== TaskType.TASK_FRAME && languageUseShareByPath.has(qualifiedPath)) {
    standalone.push({ node, qualifiedPath, taskType, languageUseShare: languageUseShareByPath.get(qualifiedPath)! });
  }
}

interface LanguageUseBackfillResult {
  clusters: LanguageUseClusterMember[][];
  standalone: LanguageUseClusterMember[];
  /** Qualified paths of language-use-eligible nodes that need a rule but have no usable own points to base a
   * share on yet (gradingScheme.maxPoints isn't a number) — content must exist with real points before
   * a language-use rule can be attached for these; never silently skipped without a trace. */
  blockedNoPoints: string[];
}

/**
 * Scans `root`'s whole subtree for language-use-eligible nodes lacking language-use (own or group membership, per
 * `existingLanguageUseSchemeRules`), computes each one's additive share from its CURRENT own points (whatever
 * is in gradingScheme.maxPoints right now — freshly written by a content generation/refine this same call,
 * or already there from before), and groups them via the all-or-nothing homogeneous-cluster rule. A share
 * is always recorded once computed — even 0, e.g. for a near-zero-point task — so such a node is never left
 * permanently without a rule (nothing to attach would otherwise mean nothing ever satisfies "has language-use").
 * A node with no usable own points at all is reported in `blockedNoPoints` instead.
 */
function computeLanguageUseBackfillForRoot(root: NestedExamTask, existingLanguageUseSchemeRules: any[]): LanguageUseBackfillResult {
  const languageUseShareByPath = new Map<string, number>();
  const blockedNoPoints: string[] = [];
  for (const s of collectSubtree(root)) {
    const node = s.node;
    const taskType = typeof (node as any).taskType === 'string' ? (node as any).taskType : undefined;
    const ratio = languageUseRatioFor(taskType);
    if (!ratio) continue;
    const qualifiedPath = s.path.join('|');
    if (hasExistingLanguageUseContent(qualifiedPath, existingLanguageUseSchemeRules)) continue;
    const gs = ((node as any).gradingScheme?.doc ?? (node as any).gradingScheme) || {};
    if (typeof gs.maxPoints !== 'number') {
      blockedNoPoints.push(qualifiedPath);
      continue;
    }
    languageUseShareByPath.set(qualifiedPath, roundToHalf(gs.maxPoints * ratio));
  }
  const clusters: LanguageUseClusterMember[][] = [];
  const standalone: LanguageUseClusterMember[] = [];
  collectLanguageUseClusters(root, [], languageUseShareByPath, clusters, standalone);
  return { clusters, standalone, blockedNoPoints };
}

/** Returns the new LanguageUseScheme entities for every standalone member (solo, 1-member) and every
 * cluster (multi-member) — callers append these to Exam.languageUseSchemeRules themselves. Every language-use
 * rule is a LanguageUseScheme now, regardless of member count. `lng` selects the template text's language
 * (the exam's own content language, not the teacher's UI language). */
function applyLanguageUseBackfill(result: LanguageUseBackfillResult, lng: string): LanguageUseScheme[] {
  const soloSchemes = result.standalone.map(
    member =>
      new LanguageUseScheme({
        _id: randomUUID(),
        criteria: languageUseTemplateFor(member.taskType, lng),
        maxPoints: member.languageUseShare,
        dependsOn: [member.qualifiedPath],
      }),
  );
  const clusterSchemes = result.clusters.map(
    members =>
      new LanguageUseScheme({
        _id: randomUUID(),
        criteria: languageUseTemplateForCluster(members, lng),
        maxPoints: members.reduce((sum, m) => sum + m.languageUseShare, 0),
        dependsOn: members.map(m => m.qualifiedPath),
      }),
  );
  return [...soloSchemes, ...clusterSchemes];
}

/** True if `root`'s subtree has any node still needing GS work: missing content, or (content present but)
 * missing language-use with usable points to backfill from. Mirrors evaluateLanguageUseSchemeSectionComplete's
 * exclusions so root selection and exam-wide completeness never disagree. */
/**
 * Exported because the `gsGeneration` job type needs the SAME answer to "does this root still have grading
 * scheme work?" in its `ready`/`desired` as the agent uses to decide what to generate. A second definition
 * would drift, and the two failure modes are a job that never runs and a job that runs forever.
 *
 * Pure: reads only the node and the rules it is handed, which is what `ready()` requires.
 */
export function rootNeedsGsWork(root: NestedExamTask, existingLanguageUseSchemeRules: any[]): boolean {
  if (gsTargets(root).length > 0) return true;
  const { clusters, standalone } = computeLanguageUseBackfillForRoot(root, existingLanguageUseSchemeRules);
  return clusters.length > 0 || standalone.length > 0;
}

/**
 * Generate grading schemes for a whole main-task subtree in one LLM call, so the model can distribute a
 * known parent point total across subtasks. `opts.feedback` switches to refine mode (regenerate every
 * gradable node in the subtree using its current scheme + feedback). Shared by run + refine.
 */
async function generateGradingSchemeForTree(
  ctx: GsTreeContext,
  root: NestedExamTask,
  // `targetKeys` (refine only) restricts which gradable nodes are regenerated — used to refine just the
  // requested subtask subtree while the rest of the root stays as read-only CONTEXT.
  opts: { feedback?: string; targetKeys?: Set<string> } = {},
): Promise<{ refinedRefs: string[]; criteriaParts: string[]; maxPointsSum: number }> {
  const empty = { refinedRefs: [] as string[], criteriaParts: [] as string[], maxPointsSum: 0 };
  const { examDocId, examContext, teacherGsRules, userLanguage, inFlight } = ctx;
  const rootKey = getTaskKey(root);
  if (inFlight.has(rootKey)) return empty;
  inFlight.add(rootKey);
  try {
    const examCollection = await getCollection(Exam);
    const isRefine = !!opts.feedback;
    const { subtree, targets, targetIds, targetTaskIds, hierarchyInputs } = buildGsHierarchyInputs(ctx, root, opts);

    // Language-use-eligible nodes get a share of their own points added on top (see computeLanguageUseBackfillForRoot
    // below) and a matching languageUseCriteria/LanguageUseScheme attached — never for a node upload or a prior run
    // already covers, and never during a refine (see the backfill call further down). Generation-mode may
    // still have work to do with zero content targets (a node whose content already exists but is missing
    // language-use), so bail out only when there is truly nothing to do this call.
    const examLanguageUseSnapshot = await examCollection.findOne({ _id: examDocId });
    const existingLanguageUseSchemeRules: any[] = Array.isArray((examLanguageUseSnapshot as any)?.languageUseSchemeRules)
      ? (examLanguageUseSnapshot as any).languageUseSchemeRules
      : [];
    if (targets.length === 0 && (isRefine || !rootNeedsGsWork(root, existingLanguageUseSchemeRules))) return empty;

    let results: GsResultEntry[] = [];
    const criteriaParts: string[] = [];
    const confirmedBefore = new Map<string, number>();

    // No content targets this call — skip the LLM entirely (nothing to generate) and fall straight through
    // to the language-use backfill below, which reads whatever own points are already stored.
    if (targets.length > 0) {
      const materialParts = await clampPayloadToByteLimit(
        await assembleHierarchyPayload(hierarchyInputs),
        LLM_PAYLOAD_SAFE_LIMIT_BYTES,
      );

      const { systemPrompt, userPrompt } = buildGradingSchemePrompts(
        {
          examContext,
          taskContext: { taskNumber: root.taskNumber, taskTypes: collectSubtreeTaskTypes(root) as any },
          feedback: opts.feedback,
          teacherRules: teacherGsRules,
        },
        userLanguage,
      );

      try {
        await markSolutionTaskStarted(examDocId, 'gradingScheme', targetTaskIds);
      } catch (e) {
        logger.error({ err: e, rootKey }, 'Failed to mark GS nodes as startedForTasks');
      }

      logger.info({ taskNumber: root.taskNumber, examDocId, isRefine }, 'Generiere Grading Scheme für Aufgabenbaum');
      const generated = await generateAiGradingScheme(systemPrompt, userPrompt, materialParts);
      results = generated.results;
      const cost = generated.cost;

      if (cost && Number.isFinite(cost) && cost > 0) {
        await addCostToExam({ usage: { cost } }, examDocId, isRefine ? 'gsGen-feedback' : 'gsGen-initial');
      }

      // Snapshot the confirmed (pre-generation) points of every node that has one, BEFORE the write-back
      // below overwrites the target leaves with the agent's output. We keep the agent's values (its criteria
      // are written to match them) and only use this snapshot to announce which tasks ended up different.
      for (const s of subtree) {
        // The declared/known total captured BEFORE the write-back overwrites target leaves' scheme points.
        const confirmed = statedTotal(s.node);
        if (typeof confirmed === 'number') confirmedBefore.set(s.id, confirmed);
      }

      const pathMap = buildPathMap(root);
      for (const r of results) {
        if (!targetIds.has(r.id)) continue; // ignore CONTEXT / unknown ids
        const node = pathMap.get(r.id);
        if (!node) continue;
        const existing = ((node as any).gradingScheme?.doc ?? (node as any).gradingScheme) || {};
        // Use the agent's own maxPoints so it always matches the criteria it generated. The required total
        // (approved/sheet value) is given to the agent in the task header and enforced via the prompt.
        const gradingScheme = new GradingScheme({
          ...(isRefine ? existing : {}),
          _id: existing._id || randomUUID(),
          examId: examDocId,
          criteria: r.criteria,
          maxPoints: r.maxPoints,
          // This agent wrote the criteria — on a refine too, which replaces what was there before.
          source: 'generated' as const,
          feedback: isRefine ? [...(Array.isArray(existing.feedback) ? existing.feedback : []), opts.feedback] : [],
        });
        (node as any).gradingScheme = gradingScheme.toObject();
        criteriaParts.push(`Aufgabe ${node.taskNumber}:\n${r.criteria}`);
      }
    }

    const maxPointsSum = targets.reduce((acc, s) => acc + Number((s.node as any).gradingScheme?.maxPoints ?? 0), 0);

    // Language-use-eligible nodes get a share of their own current points added on top (freshly written above,
    // or already there from before) and a matching languageUseCriteria/LanguageUseScheme attached — a shared
    // LanguageUseScheme group for a homogeneously language-use-eligible whole subtree (per the all-or-nothing rule),
    // or a direct own merge for a standalone node. GENERATION ONLY (never refine): a refine's content step
    // must never auto-create or auto-adjust language-use via ratio as a side effect of touching content — that's
    // not something the teacher asked for. Explicit language-use creation during refine is a separate, deliberate
    // path (refineLanguageUseCriteriaAgent, when the teacher actually names a language-use concern).
    const newLanguageUseSchemes: LanguageUseScheme[] = [];
    if (!isRefine) {
      const backfill = computeLanguageUseBackfillForRoot(root, existingLanguageUseSchemeRules);
      if (backfill.blockedNoPoints.length > 0) {
        logger.warn(
          { rootKey, blockedNoPoints: backfill.blockedNoPoints },
          'Language-use-eligible task(s) have no usable own points yet — language-use rule deferred until points exist',
        );
      }
      newLanguageUseSchemes.push(...applyLanguageUseBackfill(backfill, resolveLngCode(userLanguage)));
    }
    if (newLanguageUseSchemes.length > 0) {
      const { allocateLanguageUseSchemeDisplayNumbers } = await import('../utils/allocate-language-use-scheme-numbers.js');
      const numbers = await allocateLanguageUseSchemeDisplayNumbers(String(examDocId), newLanguageUseSchemes.length);
      newLanguageUseSchemes.forEach((scheme, index) => {
        scheme.displayNumber = numbers[index];
      });
    }

    try {
      // The grading scheme is now in place for this whole root, so its parent totals must become fully
      // derived (own + children): drop the stored FIXED total on every distributed parent so a rebalanced
      // distribution can never leave a stale fixed total behind (in the editor or in computeAggregate).
      dropDistributedParentTotals([root]);
      // This root only — see `persistRootSubtree`. Two main tasks can now generate concurrently.
      const changedTaskIds = await persistRootSubtree(String(examDocId), root, 'gradingScheme');
      // Same diff the upload path records — a generation that filled gaps changed exactly these.
      if (changedTaskIds.length > 0) await addSolutionChangedTasks(String(examDocId), 'gradingScheme', changedTaskIds);

      // The task total is a derived resolver (computeAggregate on read) — no stored parent totals remain
      // after the drop above. We only compare the freshly-generated distribution against the confirmed
      // totals to announce any adjustments below.
      // Compare every node's final total against its confirmed value; where they differ, record the task
      // so it can be announced as a deliberate complexity-based adjustment (not an error). Drop an
      // aggregator whose change is merely the roll-up of an already-listed subtask, so we name the most
      // specific task only.
      const changed = subtree.filter(s => {
        const confirmed = confirmedBefore.get(s.id);
        return typeof confirmed === 'number' && Math.abs(confirmed - effectivePoints(s.node)) > 0.001;
      });
      for (const s of changed) {
        const hasChangedDescendant = changed.some(other => other.id !== s.id && other.id.startsWith(`${s.id}.`));
        if (isAggregator(s.node) && hasChangedDescendant) continue;
        ctx.pointAdjustments.add(s.id);
      }

      if (newLanguageUseSchemes.length > 0) {
        // Appended ATOMICALLY. This used to read the stored array and write it back whole, under a comment
        // promising it would "never overwrite language-use rules another concurrent root's generation already
        // wrote" — which is exactly what a read-modify-write does. Now that roots really do run concurrently,
        // that had to become true rather than intended.
        //
        // An aggregation-pipeline update rather than `$push`, because the field can legitimately hold `null`
        // (an entity write persists a declared-but-unset array as null) and `$push` fails outright on a
        // non-array. `$ifNull` normalises missing, null and array in one atomic write — the same idiom, and
        // the same reason, as `unionInto` in `pipeline/regrade.ts`.
        const rawExams = await getRawCollection(Exam._collectionName);
        await rawExams.updateOne(
          { _id: examDocId } as any,
          [
            {
              $set: {
                languageUseSchemeRules: {
                  $concatArrays: [
                    { $ifNull: ['$languageUseSchemeRules', []] },
                    newLanguageUseSchemes.map(scheme => scheme.toObject()),
                  ],
                },
              },
            },
          ] as any,
        );
      }
      // Keep the exam-state provenance roll-up in step with the tasks this subtree just changed, so the
      // status bar reflects a mix as soon as the first generated solution lands next to an uploaded one.
      await refreshSolutionSourceState(String(examDocId));
      // One call covers one unit: this root.
      await markSolutionTasksDone(examDocId, 'gradingScheme', targetTaskIds);
    } catch (e) {
      logger.error({ err: e, rootKey }, 'Failed to persist grading schemes for subtree');
    }

    return { refinedRefs: targets.map(s => s.id), criteriaParts, maxPointsSum };
  } catch (innerGenError) {
    logger.error(
      { err: innerGenError, rootKey, context: 'generateGradingSchemeForTree' },
      'Failed to compute grading scheme subtree gracefully',
    );
    return empty;
  } finally {
    inFlight.delete(rootKey);
  }
}

/**
 * Everything a tree generation needs that does NOT vary by root. The counterpart of `buildMsTreeContext`,
 * and extracted for the same reason: the per-root entry point below builds it for one root, and
 * `runGradingSchemeAgent` builds it once for all of them.
 */
async function buildGsTreeContext(exam: any): Promise<GsTreeContext> {
  // Teacher's stored grading-scheme preferences (subject-filtered), loaded once and injected into
  // every per-task prompt.
  const teacherGsRules = await getTeacherMemoryInstructionBlock({
    userID: exam?.userID,
    category: 'grading_scheme_creation',
    subject: exam?.subject,
  });

  let userLanguage = 'English';
  try {
    const conversationCollection = await getCollection(Conversation);
    const conversation = await conversationCollection.findOne({ examId: String(exam?._id) });
    if (conversation && (conversation as any).language) {
      userLanguage = (conversation as any).language;
    }
  } catch (convErr) {
    logger.error(
      { err: convErr, examId: String(exam?._id), context: 'generateGS' },
      'Failed to fetch conversation for exam',
    );
  }

  return {
    examDocId: exam._id,
    examContext: {
      subject: exam?.subject || 'Nicht angegeben',
      topic: exam?.topic || 'Nicht angegeben',
      classLevel: exam?.class || 'Nicht angegeben',
    },
    teacherGsRules,
    userLanguage,
    allAdditionalInfo: normalizeAdditionalInfoForPrompt((exam as any)?.additionalInformation),
    allTaskAdditionalInfoIds: collectAllTaskAdditionalInfoIds(exam.tasks as any[]),
    // In-flight tracking keys on the ROOT task (one LLM call covers a whole subtree).
    inFlight: new Set<string>(),
    pointAdjustments: new Set<string>(),
  };
}

/** Why a per-root generation did nothing. All three outcomes are normal, none is a failure. */
export type GsRootSkip = 'root-gone' | 'already-covered' | 'master-solution-not-ready';

/**
 * Generates the grading scheme for ONE top-level main task — the counterpart of
 * `generateMasterSolutionForRootTask`, and the same reasoning applies (see there).
 *
 * The one difference that matters: a GS root additionally waits on its OWN subtree's master solutions, not on
 * the exam's MS run as a whole. That per-root dependency is why splitting pays here — root 3's grading scheme
 * starts as soon as root 3 has a master solution, instead of after the last root of the MS run.
 */
export async function generateGradingSchemeForRootTask(
  examId: string,
  taskKey: string,
): Promise<{ generated: boolean; skipped?: GsRootSkip }> {
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  if (!exam) throw new Error(`Exam mit ID ${examId} nicht gefunden.`);

  const root = ((exam.tasks || []) as NestedExamTask[]).find(task => getTaskKey(task) === taskKey);
  if (!root) return { generated: false, skipped: 'root-gone' };
  // Both re-checked after the claim, for the same reason as in the MS agent: `ready()` ran before it.
  if (!subtreeMasterSolutionReady(root)) return { generated: false, skipped: 'master-solution-not-ready' };
  if (!rootNeedsGsWork(root, ((exam as any).languageUseSchemeRules as any[]) ?? [])) {
    return { generated: false, skipped: 'already-covered' };
  }

  const ctx = await buildGsTreeContext(exam);
  await generateGradingSchemeForTree(ctx, root, {});
  return { generated: true };
}

/**
 * Opens a grading-scheme GENERATION run without doing any of it — the counterpart of
 * `openMasterSolutionGenerationRun`, and the same reasoning applies (see there).
 */
export async function openGradingSchemeGenerationRun(examId: string): Promise<number> {
  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  if (!exam) throw new Error(`Exam mit ID ${examId} nicht gefunden.`);

  const roots = solutionUnitRoots((exam.tasks || []) as NestedExamTask[]);
  // See `openMasterSolutionGenerationRun`, including why `beginProgressRun` is the dispatch's job and not this one's.
  await startSolutionProgress(exam._id, 'gradingScheme', {
    producer: 'generation',
    total: solutionUnitNodeCount((exam.tasks || []) as NestedExamTask[]),
    message: 'Generiere Bewertungskriterien...',
  });
  logger.info({ examId, roots: roots.length }, 'Opened grading scheme generation run');
  return roots.length;
}

/** See `runMasterSolutionAgent`: the in-process whole-run form, superseded by the `gsGeneration` job type. */
export async function runGradingSchemeAgent(examId: string): Promise<boolean> {
  try {
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam) {
      throw new Error(`Exam mit ID ${examId} nicht gefunden.`);
    }
    const examDocId = exam._id;

    if (!exam.tasks || exam.tasks.length === 0) {
      logger.warn('Keine Tasks im Exam gefunden. Starte GS-Agent im Wartemodus.');
    }

    const ctx = await buildGsTreeContext(exam);
    const inFlightTaskKeys = ctx.inFlight;
    const generateForTree = (root: NestedExamTask, opts: { feedback?: string } = {}) =>
      generateGradingSchemeForTree(ctx, root, opts);

    // Opens the run and keeps whatever units are already done — like the master solution, this agent only
    // fills the tasks that have no grading scheme yet.
    await startSolutionProgress(examDocId, 'gradingScheme', {
      producer: 'generation',
      total: solutionUnitNodeCount((exam.tasks || []) as NestedExamTask[]),
      message: 'Generiere Bewertungskriterien...',
    });

    logger.info({ examId }, 'Starte Grading Scheme Generierung für Exam');

    // Generate one whole subtree per top-level main task, once its subtree master solutions are ready.
    const rootsInitial = (exam.tasks || []) as NestedExamTask[];
    for (const root of rootsInitial) {
      if (inFlightTaskKeys.has(getTaskKey(root))) continue;
      if (!rootNeedsGsWork(root, ((exam as any).languageUseSchemeRules as any[]) ?? [])) continue;
      if (!subtreeMasterSolutionReady(root)) continue;
      await generateForTree(root);
    }

    const sub = watchQuery(
      Exam._collectionName,
      { _id: examId },
      { limit: 1, projection: { examState: 1, tasks: 1, languageUseSchemeRules: 1 } },
    ).subscribe({
      next: rows => {
        void (async () => {
          try {
            const doc = Array.isArray(rows) ? (rows[0] as any) : rows;
            if (!doc) return;

            const roots = (doc.tasks || []) as NestedExamTask[];

            try {
              await setSolutionTotal(examDocId, 'gradingScheme', solutionUnitNodeCount(roots));
              await setSolutionMessage(examDocId, 'gradingScheme', 'Generiere Bewertungskriterien...');
            } catch (e) {
              logger.error({ err: e }, 'Failed to keep the GS unit total/message in sync');
            }

            // Start each main-task subtree whose master solutions are all ready and whose GS (content or
            // language-use) is missing.
            const languageUseSchemeRules = (doc.languageUseSchemeRules as any[]) ?? [];
            const rootsToStart = roots.filter(
              root =>
                !inFlightTaskKeys.has(getTaskKey(root)) &&
                rootNeedsGsWork(root, languageUseSchemeRules) &&
                subtreeMasterSolutionReady(root),
            );

            for (const root of rootsToStart) {
              try {
                await generateForTree(root);
              } catch (e) {
                logger.error({ err: e, taskKey: getTaskKey(root) }, 'Error generating GS subtree from subscription');
              }
            }

            const taskExtractionStatus = doc.examState?.taskExtractionProgress?.status;
            // One state per artifact, so the upstream master solution is read without first having to
            // decide whether it was uploaded or generated.
            const upstreamStatus = doc.examState?.masterSolutionProgress?.status;
            const upstreamFinished = Number((doc.examState?.masterSolutionProgress?.createdForTasks || []).length);

            if (taskExtractionStatus !== StepState.COMPLETED) {
              return;
            }

            if (upstreamStatus !== StepState.COMPLETED) {
              return;
            }

            // Roots whose master solutions are ready but whose grading scheme (content or language-use) is not
            // yet complete.
            const remainingNotCreated = roots.filter(
              root => subtreeMasterSolutionReady(root) && rootNeedsGsWork(root, languageUseSchemeRules),
            );

            if (upstreamFinished > 0) {
              try {
                await setSolutionMessage(
                  examDocId,
                  'gradingScheme',
                  'Generiere Bewertungskriterien aus verfügbaren Musterlösungen...',
                );
              } catch (e) {
                logger.error({ err: e }, 'Failed to update GS progress message from upstream finished signal');
              }
            }

            if (remainingNotCreated.length === 0 && inFlightTaskKeys.size === 0) {
              try {
                await examCollection.updateOne({ _id: examDocId }, { $set: { status: 'gradingSchemeNeedsReview' } });
                await setSolutionStatus(
                  examDocId,
                  'gradingScheme',
                  StepState.COMPLETED,
                  'Bewertungskriterien erfolgreich generiert',
                );
              } catch (e) {
                logger.error({ err: e }, 'Failed to set GS progress to COMPLETED');
              }
              // Generation done — surface it to the agent (gated/batched inside updateSetupStep).
              await updateSetupStep(examDocId, {
                gradingScheme: {
                  type: 'generation',
                  reupload: false,
                  pointAdjustments: Array.from(ctx.pointAdjustments),
                },
              });
              sub.unsubscribe();
            }
          } catch (subscriptionLoopError) {
            logger.error(
              { err: subscriptionLoopError },
              'Critical background thread iteration caught inside watchQuery loop',
            );
          }
        })();
      },
      error: err => {
        logger.error({ err }, 'watchQuery subscription error for GS agent');
      },
    });

    return true;
  } catch (error) {
    logger.error({ err: error }, 'Fehler im Grading Scheme Agent');
    try {
      await setSolutionStatus(examId, 'gradingScheme', StepState.ERROR, 'Ein Fehler ist aufgetreten');
    } catch (e) {
      logger.error({ err: e, examId }, 'Failed to update exam state after GS agent error');
    }
    return false;
  }
}

export async function refineGradingSchemeAgent(
  examId: string,
  taskNumbers: string[],
  feedback: string,
): Promise<{
  success: boolean;
  newCriteria?: string;
  newMaxPoints?: number;
  error?: string;
  refinedTaskRefs?: string[];
}> {
  logger.info(
    { examId, taskNumbers: JSON.stringify(taskNumbers), feedback, context: 'DEBUG' },
    'refineGradingSchemeAgent gestartet mit eingehenden Parametern',
  );

  try {
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });

    if (!exam?.tasks) {
      logger.error({ context: 'DEBUG' }, 'Fehler: Klausur oder Aufgaben nicht in der DB gefunden.');
      return { success: false, error: 'Klausur oder Aufgaben nicht gefunden.' };
    }

    // Teacher's stored grading-scheme preferences (subject-filtered), injected into the refine prompt.
    const teacherGsRules = await getTeacherMemoryInstructionBlock({
      userID: exam?.userID,
      category: 'grading_scheme_creation',
      subject: exam?.subject,
    });

    let userLanguage = 'English';
    if (examId) {
      try {
        const conversationCollection = await getCollection(Conversation);
        const conversation = await conversationCollection.findOne({ examId });
        if (conversation && (conversation as any).language) {
          userLanguage = (conversation as any).language;
        }
      } catch (convErr) {
        logger.error(
          { err: convErr, examId: String(examId), context: 'generateGS' },
          'Failed to fetch conversation for exam',
        );
      }
    }

    const requestedTaskNumbers = taskNumbers.map(value => String(value ?? '').trim()).filter(Boolean);
    const searchTaskNums = normalizeTaskRefs(taskNumbers);

    if (searchTaskNums.length === 0) {
      return { success: false, error: 'Keine gültige Aufgabennummer übergeben.' };
    }

    // Resolve each requested task to its top-level main task (root). Refine regenerates the whole
    // subtree in one call so the point distribution stays consistent across siblings.
    const findWithRoot = (
      tasks: NestedExamTask[],
      searchTaskNum: string,
      rootTask: NestedExamTask | null,
      parentTaskPath = '',
    ): { root: NestedExamTask; node: NestedExamTask; matchedRef: string } | null => {
      for (const t of tasks) {
        const currentTaskNum = normalizeTaskRefSegment(t.taskNumber);
        const currentTaskPath = appendTaskRefSegment(parentTaskPath, currentTaskNum);
        const effectiveRoot = rootTask ?? t;
        if (currentTaskNum === searchTaskNum || currentTaskPath === searchTaskNum) {
          return { root: effectiveRoot, node: t, matchedRef: currentTaskPath || currentTaskNum };
        }
        if (t.subTasks) {
          const found = findWithRoot(t.subTasks, searchTaskNum, effectiveRoot, currentTaskPath);
          if (found) return found;
        }
      }
      return null;
    };

    // Each affected root is sent whole (for sibling context + grouping totals), but only the gradable
    // nodes of the specifically requested subtree(s) are regenerated — collected here as `targetKeys`.
    const affectedRoots = new Map<string, NestedExamTask>();
    const targetKeysByRoot = new Map<string, Set<string>>();
    const resolvedRefs: string[] = [];
    const notFoundTaskNums: string[] = [];
    for (const searchTaskNum of searchTaskNums) {
      const found = findWithRoot(exam.tasks as NestedExamTask[], searchTaskNum, null);
      if (!found) {
        notFoundTaskNums.push(searchTaskNum);
        continue;
      }
      resolvedRefs.push(found.matchedRef);
      const rootKey = getTaskKey(found.root);
      affectedRoots.set(rootKey, found.root);
      // Per-node keys — the actual progress unit, now that it's the node rather than the root (see
      // SolutionProgress's doc comment). Grouped by root only because `generateGradingSchemeForTree`
      // below is still called once per root.
      const keys = targetKeysByRoot.get(rootKey) ?? new Set<string>();
      for (const s of gradableSubtreeNodes(found.node)) keys.add(getTaskKey(s.node));
      targetKeysByRoot.set(rootKey, keys);
    }

    if (notFoundTaskNums.length > 0) {
      logger.error(
        { notFoundTaskNums: notFoundTaskNums.join(', '), context: 'DEBUG' },
        'Abbruch: Aufgabe(n) nicht gefunden',
      );
      return { success: false, error: `Aufgabe(n) nicht gefunden: ${notFoundTaskNums.join(', ')}` };
    }

    const allAdditionalInfo = normalizeAdditionalInfoForPrompt((exam as any)?.additionalInformation);
    const allTaskAdditionalInfoIds = collectAllTaskAdditionalInfoIds(exam.tasks as any[]);

    // Reset progress: drop exactly the nodes being redone (not their untouched siblings under the same
    // root) so they count as outstanding again; the total is the exam's unit count either way. See the
    // master-solution refine: provenance lives per task, not here.
    await dropSolutionTasks(exam._id, 'gradingScheme', [...targetKeysByRoot.values()].flatMap(keys => [...keys]));
    // `refine`, not `generation` — see `SolutionProducer` and `ms-generation-agent`'s refine.
    await startSolutionProgress(exam._id, 'gradingScheme', {
      producer: 'refine',
      total: solutionUnitNodeCount(exam.tasks as NestedExamTask[]),
      message: 'Bewertungskriterien werden überarbeitet...',
    });

    const ctx: GsTreeContext = {
      examDocId: exam._id,
      examContext: {
        subject: exam?.subject || 'Nicht angegeben',
        topic: exam?.topic || 'Nicht angegeben',
        classLevel: exam?.class || 'Nicht angegeben',
      },
      teacherGsRules,
      userLanguage,
      allAdditionalInfo,
      allTaskAdditionalInfoIds,
      inFlight: new Set<string>(),
      pointAdjustments: new Set<string>(),
    };

    const refinedCriteriaParts: string[] = [];
    let totalMaxPoints = 0;

    for (const [rootKey, root] of affectedRoots) {
      const res = await generateGradingSchemeForTree(ctx, root, {
        feedback: feedback || 'Kein Feedback gegeben.',
        targetKeys: targetKeysByRoot.get(rootKey),
      });
      refinedCriteriaParts.push(...res.criteriaParts);
      totalMaxPoints += res.maxPointsSum;
    }

    await setSolutionStatus(
      exam._id,
      'gradingScheme',
      StepState.COMPLETED,
      'Bewertungskriterien erfolgreich überarbeitet',
    );

    logger.info({ examId: exam._id, refs: resolvedRefs.join(', '), context: 'GS_REFINE' }, 'SAVED_PERSISTED');
    if (refinedCriteriaParts.length === 0) {
      logger.warn(
        { context: 'GS_REFINE' },
        'Aborting automatic regrading flow because no tasks were successfully updated.',
      );
      return { success: false, error: 'Die Überarbeitung der Kriterien ist fehlgeschlagen.' };
    }

    // The downstream regrading of student submissions is fired by the callers (the refine_grading_scheme
    // tool directly, or the master-solution refinement cascade), not here; we just hand back which task
    // refs were refined.
    logger.info(
      { requestedTaskNumbers: requestedTaskNumbers.join(', '), context: 'DEBUG' },
      'Grading Scheme fuer Tasks erfolgreich aktualisiert',
    );
    const combinedCriteria =
      refinedCriteriaParts.length === 1
        ? (refinedCriteriaParts[0] ?? '').replace(/^Aufgabe\s+[^:]+:\n/, '')
        : refinedCriteriaParts.join('\n\n');
    return {
      success: true,
      newCriteria: combinedCriteria,
      newMaxPoints: totalMaxPoints,
      refinedTaskRefs: resolvedRefs,
    };
  } catch (error) {
    logger.error({ err: error, examId, context: 'DEBUG' }, 'Fehler beim Refine Grading Scheme Agent fuer Exam');
    return { success: false, error: 'Interner Serverfehler.' };
  }
}

/**
 * Refines language-use grading rules based on teacher feedback — the language-use-focused sibling of
 * refineGradingSchemeAgent, for the requested task numbers. Every language-use rule is a LanguageUseScheme
 * (Exam.languageUseSchemeRules), solo (1-member) or multi-member alike. Branches per requested task:
 * - Membership in an EXISTING LanguageUseScheme: every DISTINCT scheme covering any requested task is
 *   refined once (using its own current text, not per-task), and written back to
 *   Exam.languageUseSchemeRules by _id — this can affect tasks beyond the ones requested, since a
 *   multi-member scheme spans every task in its dependsOn list.
 * - No existing rule: a NEW LanguageUseScheme is created for every requested task still missing one — a
 *   deliberate, explicit creation (the teacher named this task with target 'languageUse'/'both'), unlike
 *   generateGradingSchemeForTree's content-refine path, which never creates or adjusts language-use as a side
 *   effect of touching content. Re-derives the full all-or-nothing homogeneous cluster shape across each
 *   task's whole top-level root (not just the requested subset) — so naming one member of what should be a
 *   multi-task group still produces one shared group covering all of them, matching what fresh generation
 *   would have produced. The static template and ratio-based share are only a STARTING point handed to the
 *   same LLM edit used for an existing rule (buildLanguageUseCriteriaRefinementPrompts) — never applied as the
 *   final answer on their own — so an explicit instruction in the feedback (e.g. a stated point value) is
 *   honored exactly like refining an existing rule would, instead of being silently overridden by the ratio
 *   guess. A requested task with no usable own points yet (content criteria missing entirely, or
 *   present without a points value) cannot get a share computed and is reported in `blockedTaskRefs`
 *   instead of a silent no-op.
 */

/**
 * Per-requested-ref resolution of which LanguageUseScheme(s) a language-use refine should touch. A task can
 * belong to more than one scheme, so a naive "every scheme matching any requested ref" (the union) would
 * silently sweep in a co-member scheme the teacher never asked about — a request naming several UNRELATED
 * tasks in different schemes (e.g. two solo rules) must still touch all of them, while a single ambiguous
 * ref must not silently touch more than one candidate.
 *
 * NOT `findLanguageUseSchemesForTask` (shared/grammar-rules-resolution.ts) — that one matches by exact
 * qualified-path equality against an already-resolved tree path. This caller only ever has the LLM's
 * freeform ref string, so it matches via `normalizeTaskRef` the same way `findTask` above does.
 *
 * Returns `groupsToRefine` (deduped by `_id`) for the caller to act on, and `ambiguousRefs` — refs that
 * matched more than one scheme and were not resolved by `groupNumber`/`languageUseSchemeId` (absent, or not
 * among that ref's own candidates) — for the caller to ask the teacher about instead of guessing. Each
 * ambiguous candidate's `otherDependsOn` already excludes the entries matching the ambiguous ref itself,
 * for building a natural-language "this rule ALSO covers X" description without ever mentioning `_id`.
 */
export function resolveLanguageUseSchemesForRequestedRefs(
  taskRefs: string[],
  languageUseSchemeRules: any[],
  opts: { languageUseSchemeId?: string; groupNumber?: number } = {},
): {
  groupsToRefine: any[];
  ambiguousRefs: Array<{
    ref: string;
    matchingDependsOn: string;
    candidates: Array<{ _id: string; displayNumber: number | null; maxPoints: number; otherDependsOn: string[] }>;
  }>;
} {
  const { languageUseSchemeId, groupNumber } = opts;
  const searchTaskNums = normalizeTaskRefs(taskRefs);
  const matchesRef = (tn: string, ref: string) => normalizeTaskRef(tn) === ref;
  const candidatesFor = (ref: string) =>
    languageUseSchemeRules.filter(scheme => Array.isArray(scheme?.dependsOn) && scheme.dependsOn.some((tn: string) => matchesRef(tn, ref)));

  const groupsById = new Map<string, any>();
  const ambiguousRefs: Array<{
    ref: string;
    matchingDependsOn: string;
    candidates: Array<{ _id: string; displayNumber: number | null; maxPoints: number; otherDependsOn: string[] }>;
  }> = [];

  for (const ref of searchTaskNums) {
    const candidates = candidatesFor(ref);
    if (candidates.length <= 1) {
      const only = candidates[0];
      if (only) groupsById.set(String(only._id), only);
      continue;
    }
    const chosen =
      (typeof groupNumber === 'number' ? candidates.find(c => Number(c.displayNumber) === groupNumber) : undefined) ??
      (languageUseSchemeId ? candidates.find(c => String(c._id) === languageUseSchemeId) : undefined);
    if (chosen) {
      groupsById.set(String(chosen._id), chosen);
    } else {
      const matchingDependsOn = (candidates[0].dependsOn as string[]).find(tn => matchesRef(tn, ref))!;
      ambiguousRefs.push({
        ref,
        matchingDependsOn,
        candidates: candidates.map(c => ({
          _id: String(c._id),
          displayNumber: typeof c.displayNumber === 'number' ? c.displayNumber : null,
          maxPoints: Number(c.maxPoints) || 0,
          otherDependsOn: (c.dependsOn as string[]).filter(tn => !matchesRef(tn, ref)),
        })),
      });
    }
  }

  return { groupsToRefine: Array.from(groupsById.values()), ambiguousRefs };
}

export async function refineLanguageUseCriteriaAgent(
  examId: string,
  taskNumbers: string[],
  feedback: string,
  disambiguation: { languageUseSchemeId?: string; groupNumber?: number } = {},
): Promise<{
  success: boolean;
  error?: string;
  /** Every task number covered by a refined or newly created LanguageUseScheme (may extend beyond the
   * requested set). */
  refinedGroupTaskRefs: string[];
  /** Requested task refs that need a language-use rule but have no usable own points to base one on yet. */
  blockedTaskRefs: string[];
  /** Requested refs that matched more than one LanguageUseScheme and were left untouched — the caller
   * should already have resolved this before dispatching; present only as a defensive signal. */
  ambiguousRefs?: Array<{ ref: string; matchingDependsOn: string; candidates: any[] }>;
}> {
  const empty = {
    refinedGroupTaskRefs: [] as string[],
    blockedTaskRefs: [] as string[],
  };
  try {
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    if (!exam?.tasks) return { success: false, error: 'Klausur oder Aufgaben nicht gefunden.', ...empty };

    const teacherGsRules = await getTeacherMemoryInstructionBlock({
      userID: exam?.userID,
      category: 'grading_scheme_creation',
      subject: exam?.subject,
    });

    let userLanguage = 'English';
    try {
      const conversationCollection = await getCollection(Conversation);
      const conversation = await conversationCollection.findOne({ examId });
      if (conversation && (conversation as any).language) userLanguage = (conversation as any).language;
    } catch (convErr) {
      logger.error({ err: convErr, examId, context: 'refineLanguageUseCriteria' }, 'Failed to fetch conversation for exam');
    }

    const searchTaskNums = normalizeTaskRefs(taskNumbers);
    if (searchTaskNums.length === 0) {
      return { success: false, error: 'Keine gültige Aufgabennummer übergeben.', ...empty };
    }

    const findTask = (
      tasks: NestedExamTask[],
      searchTaskNum: string,
      parentTaskPath = '',
    ): { task: NestedExamTask; matchedRef: string } | null => {
      for (const t of tasks) {
        const currentTaskNum = normalizeTaskRefSegment(t.taskNumber);
        const currentTaskPath = appendTaskRefSegment(parentTaskPath, currentTaskNum);
        if (currentTaskNum === searchTaskNum || currentTaskPath === searchTaskNum) {
          return { task: t, matchedRef: currentTaskPath || currentTaskNum };
        }
        if (t.subTasks) {
          const found = findTask(t.subTasks, searchTaskNum, currentTaskPath);
          if (found) return found;
        }
      }
      return null;
    };

    const examContext = {
      subject: exam?.subject || 'Nicht angegeben',
      topic: exam?.topic || 'Nicht angegeben',
      classLevel: exam?.class || 'Nicht angegeben',
    };

    const languageUseSchemeRules: any[] = Array.isArray((exam as any).languageUseSchemeRules)
      ? (exam as any).languageUseSchemeRules
      : [];
    const { groupsToRefine, ambiguousRefs } = resolveLanguageUseSchemesForRequestedRefs(
      taskNumbers,
      languageUseSchemeRules,
      disambiguation,
    );
    // The chat tool is expected to have already resolved any ambiguity before dispatching here (this is a
    // defensive second application of the same resolver, for the rare pre-check/execution race window —
    // see the tool's own pre-check). An unresolved ambiguous ref contributes nothing to groupsToRefine and
    // is surfaced explicitly here rather than being reported as "no rule found" (misleading — two-plus
    // rules exist, just unresolved).
    if (ambiguousRefs.length > 0 && groupsToRefine.length === 0) {
      return {
        success: false,
        error: 'Für die angegebene(n) Aufgabe(n) gibt es mehr als eine Regel für die sprachliche Leistung — nicht eindeutig, welche gemeint ist.',
        ...empty,
        ambiguousRefs,
      };
    }

    const refinedGroupTaskRefs: string[] = [];
    const updatedLanguageUseSchemeRules = languageUseSchemeRules.map(scheme => ({ ...scheme }));
    let languageUseSchemeRulesChanged = false;

    for (const scheme of groupsToRefine) {
      const { systemPrompt, userPrompt } = buildLanguageUseCriteriaRefinementPrompts(
        {
          examContext,
          taskNumbers: Array.isArray(scheme.dependsOn) ? scheme.dependsOn : [],
          oldCriteria: scheme.criteria || '',
          oldMaxPoints: Number(scheme.maxPoints) || 0,
          feedback: feedback || 'Kein Feedback gegeben.',
          teacherRules: teacherGsRules,
        },
        userLanguage,
      );

      const { results, cost } = await generateAiGradingScheme(systemPrompt, userPrompt, []);
      if (cost && Number.isFinite(cost) && cost > 0) {
        await addCostToExam({ usage: { cost } }, examId, 'gsGen-languageUseGroupRefine');
      }
      const criteria = results[0]?.criteria ?? scheme.criteria;
      const maxPoints = results[0]?.maxPoints ?? scheme.maxPoints;

      const idx = updatedLanguageUseSchemeRules.findIndex(s => String(s._id) === String(scheme._id));
      if (idx >= 0) {
        updatedLanguageUseSchemeRules[idx] = { ...updatedLanguageUseSchemeRules[idx], criteria, maxPoints };
        languageUseSchemeRulesChanged = true;
      }
      refinedGroupTaskRefs.push(...(Array.isArray(scheme.dependsOn) ? scheme.dependsOn : []));
    }

    // 3) Create a language-use rule for any requested task that STILL has no rule (per the current
    // in-memory state, reflecting whatever the refine loop above just did) — re-deriving the full
    // homogeneous-cluster shape across each task's whole top-level root (not just the
    // requested subset). The static template + ratio share are only a STARTING point handed to the SAME
    // LLM edit used for existing rules — never the final answer on their own — so an explicit instruction
    // in the feedback (a point value, a stricter tone, etc.) is honored exactly like refining an existing
    // rule would; with no such instruction the model is told to keep the starting values unchanged.
    const findRootContaining = (roots: NestedExamTask[], target: NestedExamTask): NestedExamTask | null => {
      for (const root of roots) {
        if (collectSubtree(root).some(s => s.node === target)) return root;
      }
      return null;
    };

    const affectedRootsForCreation = new Map<string, NestedExamTask>();
    const requestedQualifiedPaths = new Set<string>();
    for (const searchTaskNum of searchTaskNums) {
      const found = findTask(exam.tasks as NestedExamTask[], searchTaskNum);
      if (!found) continue;
      const root = findRootContaining(exam.tasks as NestedExamTask[], found.task);
      if (!root) continue;
      affectedRootsForCreation.set(getTaskKey(root), root);
      const ownPath = collectSubtree(root).find(s => s.node === found.task)?.path;
      if (ownPath) requestedQualifiedPaths.add(ownPath.join('|'));
    }

    const blockedQualifiedPaths: string[] = [];
    const languageUseTemplateLng = resolveLngCode(userLanguage);
    const { allocateLanguageUseSchemeDisplayNumbers } = await import('../utils/allocate-language-use-scheme-numbers.js');
    for (const root of affectedRootsForCreation.values()) {
      const backfill = computeLanguageUseBackfillForRoot(root, updatedLanguageUseSchemeRules);

      for (const member of backfill.standalone) {
        const { systemPrompt, userPrompt } = buildLanguageUseCriteriaRefinementPrompts(
          {
            examContext,
            taskNumbers: [String((member.node as any).taskNumber)],
            oldCriteria: languageUseTemplateFor(member.taskType, languageUseTemplateLng),
            oldMaxPoints: member.languageUseShare,
            feedback: feedback || 'Kein Feedback gegeben.',
            teacherRules: teacherGsRules,
          },
          userLanguage,
        );
        const { results, cost } = await generateAiGradingScheme(systemPrompt, userPrompt, []);
        if (cost && Number.isFinite(cost) && cost > 0) {
          await addCostToExam({ usage: { cost } }, examId, 'gsGen-languageUseCreate');
        }
        const [standaloneDisplayNumber] = await allocateLanguageUseSchemeDisplayNumbers(examId, 1);
        updatedLanguageUseSchemeRules.push(
          new LanguageUseScheme({
            _id: randomUUID(),
            criteria: results[0]?.criteria ?? languageUseTemplateFor(member.taskType, languageUseTemplateLng),
            maxPoints: results[0]?.maxPoints ?? member.languageUseShare,
            dependsOn: [member.qualifiedPath],
            displayNumber: standaloneDisplayNumber,
          }).toObject(),
        );
        languageUseSchemeRulesChanged = true;
        refinedGroupTaskRefs.push(member.qualifiedPath);
      }

      for (const members of backfill.clusters) {
        const startingMaxPoints = members.reduce((sum, m) => sum + m.languageUseShare, 0);
        const startingCriteria = languageUseTemplateForCluster(members, languageUseTemplateLng);
        const { systemPrompt, userPrompt } = buildLanguageUseCriteriaRefinementPrompts(
          {
            examContext,
            taskNumbers: members.map(m => m.qualifiedPath),
            oldCriteria: startingCriteria,
            oldMaxPoints: startingMaxPoints,
            feedback: feedback || 'Kein Feedback gegeben.',
            teacherRules: teacherGsRules,
          },
          userLanguage,
        );
        const { results, cost } = await generateAiGradingScheme(systemPrompt, userPrompt, []);
        if (cost && Number.isFinite(cost) && cost > 0) {
          await addCostToExam({ usage: { cost } }, examId, 'gsGen-languageUseGroupCreate');
        }
        const [clusterDisplayNumber] = await allocateLanguageUseSchemeDisplayNumbers(examId, 1);
        updatedLanguageUseSchemeRules.push(
          new LanguageUseScheme({
            _id: randomUUID(),
            criteria: results[0]?.criteria ?? startingCriteria,
            maxPoints: results[0]?.maxPoints ?? startingMaxPoints,
            dependsOn: members.map(m => m.qualifiedPath),
            displayNumber: clusterDisplayNumber,
          }).toObject(),
        );
        languageUseSchemeRulesChanged = true;
        refinedGroupTaskRefs.push(...members.map(m => m.qualifiedPath));
      }

      blockedQualifiedPaths.push(...backfill.blockedNoPoints);
    }

    // Only surface a hard error for a task the teacher actually named — an unrelated blocked sibling
    // elsewhere in the same root (not requested, not ready yet) is left for later, same as how missing
    // points never blocks content completeness either.
    const blockedTaskRefs = Array.from(
      new Set(blockedQualifiedPaths.filter(qp => requestedQualifiedPaths.has(qp)).map(normalizeTaskRef)),
    );

    if (languageUseSchemeRulesChanged) {
      await examCollection.updateOne({ _id: examId }, { $set: { languageUseSchemeRules: updatedLanguageUseSchemeRules } });
    }

    if (!languageUseSchemeRulesChanged) {
      return {
        success: false,
        error:
          blockedTaskRefs.length > 0
            ? `Für ${blockedTaskRefs.join(', ')} kann noch keine Regel für die sprachliche Leistung erstellt werden, da noch keine Bewertungskriterien mit Punkten vorliegen. Bitte zuerst das inhaltliche Bewertungsschema erstellen.`
            : 'Für die angegebenen Aufgaben wurde keine Regel für die sprachliche Leistung gefunden.',
        ...empty,
      };
    }

    return {
      success: true,
      refinedGroupTaskRefs: Array.from(new Set(refinedGroupTaskRefs.map(normalizeTaskRef))),
      blockedTaskRefs,
    };
  } catch (error) {
    logger.error(
      { err: error, examId, context: 'refineLanguageUseCriteriaAgent' },
      'Fehler beim Refine Language Use Criteria Agent',
    );
    return { success: false, error: 'Interner Serverfehler.', ...empty };
  }
}
